/**
 * User + Appearance settings pages — RO-3-09 / #44302.
 *
 * Ports of:
 *   settings/user/profile            (57 LOC DELEGATOR)
 *   settings/user/preferences        (21 LOC DELEGATOR)
 *   settings/user/notifications      (22 LOC DELEGATOR)
 *   settings/user/keyboard-shortcuts (39 LOC DELEGATOR, pure static)
 *   settings/appearance              (17 LOC DELEGATOR)
 *
 * Every page wraps content in `SettingsPageShell` — the ONE shell for the
 * section (title says "verify the shell ports once"; it already lives in
 * packages/ui and is reused here, not reimplemented).
 *
 * Transport change (PRD §4.1):
 *   `getAppearance()`          → `useApi(userSettings.getAppearance)`
 *   `getCurrentUserActor()`    → `useApi(userSettings.getProfile)`  (pure read)
 *   `getUserPreferences()`     → `useApi(userSettings.getPreferences)`
 *   `getNotificationMatrix()`  → `useApi(userSettings.getNotifications)`
 *   server actions             → `apiCall` against the RO-2-09d POSTs
 *
 * Preferences + notifications save on change (immediate-action controls), same
 * as the Next originals. Keyboard shortcuts are pure static content.
 *
 * Live Next pages under `excellent/app/settings/user/**` and
 * `settings/appearance` are UNTOUCHED (D6 — repointing is client cutover).
 */

import * as React from "react";
import { useState, useTransition } from "react";
import { createRoute } from "@tanstack/react-router";
import {
  AppearanceForm,
  FormField,
  Input,
  KeyboardShortcutsList,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsForm,
  SettingsPageShell,
  SettingsRow,
  SettingsSection,
  Switch,
  cn,
  type ShortcutGroup,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import { userSettings } from "@excellent/ui/api/contracts/user-settings";
import { rootRoute } from "./__root";

// ── /settings/user/profile ──────────────────────────────────────────────────

function UserProfile() {
  const { data, isLoading, error } = useApi(userSettings.getProfile, {});

  async function onSave(fd: FormData): Promise<void> {
    await apiCall(userSettings.saveProfileForm, {
      body: { display_name: String(fd.get("display_name") ?? "") },
    });
  }

  return (
    <SettingsPageShell
      title="Profile"
      description="How you appear across the app — on activity rows, comments, and the avatar menu."
    >
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : (
        <SettingsForm
          action={onSave}
          saveLabel="Save profile"
          key={data?.external_id ?? "profile"}
        >
          <SettingsSection
            id="profile"
            title="Profile"
            description="How you appear across the app — on activity rows, comments, and the avatar menu."
          >
            <div className="max-w-md space-y-4">
              <FormField
                label="Display name"
                htmlFor="display_name"
                help="Shown on activity rows, comments, and the avatar menu."
              >
                <Input
                  id="display_name"
                  name="display_name"
                  defaultValue={data?.display_name ?? ""}
                  placeholder="Your name"
                />
              </FormField>
              <FormField
                label="Actor ID"
                help="Your stable identifier in tasks, commits, and the ship/verify ledger. Not editable."
              >
                <code className="inline-block rounded-md border border-border/60 bg-surface-2 px-2 py-1 type-body text-foreground">
                  {data?.external_id ?? "you"}
                </code>
              </FormField>
            </div>
          </SettingsSection>
        </SettingsForm>
      )}
    </SettingsPageShell>
  );
}

// ── /settings/user/preferences ──────────────────────────────────────────────

type StartPage = "dashboard" | "play" | "last";
type TimeFormat = "12h" | "24h";
type WeekStart = "sunday" | "monday";

interface Prefs {
  startPage: StartPage;
  timeFormat: TimeFormat;
  weekStart: WeekStart;
  reduceMotion: boolean;
}

const START_PAGE_OPTIONS: Array<{ value: StartPage; label: string }> = [
  { value: "dashboard", label: "Dashboard" },
  { value: "play", label: "Play queue" },
  { value: "last", label: "Last visited" },
];
const TIME_FORMAT_OPTIONS: Array<{ value: TimeFormat; label: string }> = [
  { value: "12h", label: "12-hour" },
  { value: "24h", label: "24-hour" },
];
const WEEK_START_OPTIONS: Array<{ value: WeekStart; label: string }> = [
  { value: "sunday", label: "Sunday" },
  { value: "monday", label: "Monday" },
];

function PreferencesBody({ initial }: { initial: Prefs }) {
  const [prefs, setPrefs] = useState(initial);
  const [, startTransition] = useTransition();

  function update<K extends keyof Prefs>(
    key: K,
    value: Prefs[K],
    save: () => Promise<unknown>,
  ) {
    setPrefs((p) => ({ ...p, [key]: value }));
    startTransition(() => {
      void save();
    });
  }

  return (
    <SettingsSection
      id="preferences"
      title="Preferences"
      description="Personal defaults for how the app opens and renders for you."
    >
      <div>
        <SettingsRow
          divided
          label="Start page"
          description="Where the app opens when you arrive."
          control={
            <Select
              value={prefs.startPage}
              onValueChange={(v) =>
                update("startPage", v as StartPage, () =>
                  apiCall(userSettings.setStartPage, { body: { value: v } }),
                )
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {START_PAGE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <SettingsRow
          divided
          label="Time format"
          description="Clock format used across the app."
          control={
            <Select
              value={prefs.timeFormat}
              onValueChange={(v) =>
                update("timeFormat", v as TimeFormat, () =>
                  apiCall(userSettings.setTimeFormat, { body: { value: v } }),
                )
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_FORMAT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <SettingsRow
          divided
          label="Week starts on"
          description="First day of the week in date pickers."
          control={
            <Select
              value={prefs.weekStart}
              onValueChange={(v) =>
                update("weekStart", v as WeekStart, () =>
                  apiCall(userSettings.setWeekStart, { body: { value: v } }),
                )
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEK_START_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <SettingsRow
          label="Reduce motion"
          description="Prefer reduced animations when the OS asks for it, and always when this is on."
          control={
            <Switch
              checked={prefs.reduceMotion}
              onCheckedChange={(v) =>
                update("reduceMotion", v, () =>
                  apiCall(userSettings.setReduceMotion, { body: { value: v } }),
                )
              }
            />
          }
        />
      </div>
    </SettingsSection>
  );
}

function UserPreferences() {
  const { data, isLoading, error } = useApi(userSettings.getPreferences, {});

  return (
    <SettingsPageShell
      title="Preferences"
      description="Personal defaults for how the app opens and renders for you."
    >
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : data ? (
        <PreferencesBody
          initial={{
            startPage: data.startPage as StartPage,
            timeFormat: data.timeFormat as TimeFormat,
            weekStart: data.weekStart as WeekStart,
            reduceMotion: data.reduceMotion,
          }}
        />
      ) : null}
    </SettingsPageShell>
  );
}

// ── /settings/user/notifications ────────────────────────────────────────────

type NotificationCategory = "mentions" | "assigned" | "reviews" | "agents" | "digest";
type NotificationChannel = "inApp" | "desktop" | "email";
type Matrix = Record<NotificationCategory, Record<NotificationChannel, boolean>>;

const CATEGORIES: Array<{
  key: NotificationCategory;
  label: string;
  description: string;
}> = [
  {
    key: "mentions",
    label: "Mentions & replies",
    description: "When someone @mentions you or replies in your thread.",
  },
  {
    key: "assigned",
    label: "Assigned to me",
    description: "A task or review lands on your plate.",
  },
  {
    key: "reviews",
    label: "Review requests",
    description: "Ship/verify work waiting on your sign-off.",
  },
  {
    key: "agents",
    label: "Agent activity",
    description: "Autopilot runs, worker results, and goal updates.",
  },
  {
    key: "digest",
    label: "Weekly digest",
    description: "A Monday summary of what moved.",
  },
];

const CHANNELS: Array<{ key: NotificationChannel; label: string }> = [
  { key: "inApp", label: "In-app" },
  { key: "desktop", label: "Desktop" },
  { key: "email", label: "Email" },
];

const GRID = "grid grid-cols-[minmax(0,1fr)_4rem_4rem_4rem] items-center gap-x-2";

function NotificationsBody({ initial }: { initial: Matrix }) {
  const [matrix, setMatrix] = useState(initial);
  const [, startTransition] = useTransition();

  function toggle(
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) {
    setMatrix((m) => ({
      ...m,
      [category]: { ...m[category], [channel]: enabled },
    }));
    startTransition(() => {
      void apiCall(userSettings.setNotificationPreference, {
        body: { category, channel, enabled },
      });
    });
  }

  return (
    <SettingsSection
      id="notifications"
      title="Notifications"
      description="Choose which events reach you, and where."
    >
      <div className="space-y-0">
        <div
          className={cn(GRID, "border-b border-border/60 pb-2 type-subtext text-muted-foreground")}
        >
          <span />
          {CHANNELS.map((c) => (
            <span key={c.key} className="text-center">
              {c.label}
            </span>
          ))}
        </div>
        {CATEGORIES.map((cat, i) => (
          <div
            key={cat.key}
            className={cn(
              GRID,
              "py-3",
              i < CATEGORIES.length - 1 && "border-b border-border/60",
            )}
          >
            <div className="min-w-0 pr-3">
              <div className="type-body font-medium text-foreground">{cat.label}</div>
              <div className="type-subtext text-muted-foreground">{cat.description}</div>
            </div>
            {CHANNELS.map((ch) => (
              <div key={ch.key} className="flex justify-center">
                <Switch
                  checked={Boolean(matrix[cat.key]?.[ch.key])}
                  onCheckedChange={(v) => toggle(cat.key, ch.key, v)}
                  aria-label={`${cat.label} · ${ch.label}`}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </SettingsSection>
  );
}

function UserNotifications() {
  const { data, isLoading, error } = useApi(userSettings.getNotifications, {});

  return (
    <SettingsPageShell
      title="Notifications"
      description="Choose which events reach you, and where."
    >
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : data ? (
        <NotificationsBody initial={data as Matrix} />
      ) : null}
    </SettingsPageShell>
  );
}

// ── /settings/user/keyboard-shortcuts ───────────────────────────────────────

const GLOBAL_SHORTCUTS: ShortcutGroup = {
  label: "Global",
  shortcuts: [
    { label: "Open command palette", keys: ["⌘", "K"] },
    { label: "Quick-create issue", keys: ["c"], context: "Anywhere" },
    { label: "Open settings", keys: [","], context: "User menu" },
  ],
};

const NAV_GROUP: ShortcutGroup = {
  label: "Navigation",
  shortcuts: [
    { label: "Home / dashboard", keys: ["g", "h"] },
    { label: "Kanban board", keys: ["g", "b"] },
    { label: "Planner", keys: ["g", "p"] },
    { label: "Settings", keys: ["g", "s"] },
  ],
};

function UserKeyboardShortcuts() {
  return (
    <SettingsPageShell
      title="Keyboard shortcuts"
      description="Every keyboard binding the app currently registers."
    >
      <KeyboardShortcutsList groups={[GLOBAL_SHORTCUTS, NAV_GROUP]} />
    </SettingsPageShell>
  );
}

// ── /settings/appearance ────────────────────────────────────────────────────

function AppearanceSettings() {
  const { data, isLoading, error } = useApi(userSettings.getAppearance, {});

  async function onSave(fd: FormData): Promise<void> {
    await apiCall(userSettings.saveAppearanceForm, {
      body: {
        theme: String(fd.get("theme") ?? ""),
        density: String(fd.get("density") ?? ""),
        accent: String(fd.get("accent") ?? ""),
      },
    });
  }

  return (
    <SettingsPageShell
      title="Appearance"
      description="Theme, density, and accent — applied across the whole app."
    >
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : data ? (
        <AppearanceForm
          initial={{
            theme: data.theme as "light" | "dark" | "system",
            density: data.density as "compact" | "comfortable" | "spacious",
            accent: data.accent,
          }}
          onSave={onSave}
        />
      ) : null}
    </SettingsPageShell>
  );
}

// ── routes ──────────────────────────────────────────────────────────────────

export const settingsUserProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/user/profile",
  component: UserProfile,
});

export const settingsUserPreferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/user/preferences",
  component: UserPreferences,
});

export const settingsUserNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/user/notifications",
  component: UserNotifications,
});

export const settingsUserKeyboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/user/keyboard-shortcuts",
  component: UserKeyboardShortcuts,
});

export const settingsAppearanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/appearance",
  component: AppearanceSettings,
});
